import { useState, useEffect } from "react";
import axios from "axios";
import {
  DollarSign,
  TrendingUp,
  Building2,
  ShoppingCart,
  Users,
  Layers
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { CreditCard, Calendar } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminEarnings() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterChannel, setFilterChannel] = useState("all");
  const [date, setDate] = useState({
    from: new Date(new Date().getFullYear(), 0, 1),
    to: new Date(new Date().getFullYear(), 11, 31),
  });

  useEffect(() => {
    fetchEarnings();
  }, [date, filterChannel]);

  const fetchEarnings = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterChannel !== "all") params.channel = filterChannel;

      if (date?.from) {
        const startDay = new Date(date.from);
        startDay.setHours(0, 0, 0, 0);
        params.startDate = startDay.toISOString();
      }
      if (date?.to) {
        const endDay = new Date(date.to);
        endDay.setHours(23, 59, 59, 999);
        params.endDate = endDay.toISOString();
      }

      const response = await axios.get(`${API}/earnings/all`, { params });
      setData(response.data);
    } catch (error) {
      console.error("Failed to fetch earnings:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const platformStats = data?.platform_stats || {};
  const resellerEarningsRaw = data?.reseller_earnings || [];
  const distributorEarningsRaw = data?.distributor_earnings || [];

  // Filter out zero earnings
  const resellerEarnings = resellerEarningsRaw.filter(e => e.total_earnings > 0);
  const distributorEarnings = distributorEarningsRaw.filter(e => e.total_earnings > 0);

  const totalRev = platformStats.total_revenue || 0;
  const platformPct = totalRev > 0 ? ((platformStats.platform_share || 0) / totalRev * 100) : 0;
  const resellerPct = totalRev > 0 ? ((platformStats.reseller_share || 0) / totalRev * 100) : 0;
  const distributorPct = totalRev > 0 ? ((platformStats.distributor_share || 0) / totalRev * 100) : 0;
  // Net platform = total - reseller - distributor (what actually stays with platform)
  const netPlatformShare = (platformStats.platform_share || 0) - (platformStats.distributor_share || 0);
  const netPlatformPct = totalRev > 0 ? (netPlatformShare / totalRev * 100) : 0;

  const stats = [
    {
      label: "Total Revenue",
      value: `$${totalRev.toFixed(2)}`,
      icon: DollarSign,
      color: "bg-gradient-to-br from-indigo-500 to-indigo-600",
      iconBg: "bg-indigo-400/20",
      description: "All payments processed"
    },
    {
      label: "Net Platform Revenue",
      value: `$${netPlatformShare.toFixed(2)}`,
      icon: Building2,
      color: "bg-gradient-to-br from-emerald-500 to-emerald-600",
      iconBg: "bg-emerald-400/20",
      description: "After reseller & distributor shares"
    },
    {
      label: "Reseller Commissions",
      value: `$${(platformStats.reseller_share || 0).toFixed(2)}`,
      icon: Users,
      color: "bg-gradient-to-br from-amber-500 to-amber-600",
      iconBg: "bg-amber-400/20",
      description: `Owed: $${(platformStats.reseller_pending || 0).toFixed(2)}`
    },
    {
      label: "Distributor Commissions",
      value: `$${(platformStats.distributor_share || 0).toFixed(2)}`,
      icon: Layers,
      color: "bg-gradient-to-br from-blue-500 to-blue-600",
      iconBg: "bg-blue-400/20",
      description: "Total distributor earnings"
    },
    {
      label: "Total Orders",
      value: platformStats.total_orders || 0,
      icon: ShoppingCart,
      color: "bg-gradient-to-br from-purple-500 to-purple-600",
      iconBg: "bg-purple-400/20",
      description: "Completed orders"
    },
  ];

  return (
    <div className="space-y-8 animate-fade-in" data-testid="admin-earnings">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">
            Platform Earnings
          </h2>
          <p className="text-slate-500">Monitor revenue, reseller and distributor commissions</p>
        </div>
        <div className="flex items-end gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-[10px] text-slate-400 font-bold uppercase ml-1">Channel</label>
          <Select value={filterChannel} onValueChange={setFilterChannel}>
            <SelectTrigger className="w-[160px] bg-white"><SelectValue placeholder="All Channels" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Channels</SelectItem>
              <SelectItem value="edisplay">e-Displayroom</SelectItem>
              <SelectItem value="infinity">Infinity Touch</SelectItem>
              <SelectItem value="funeral">Funeral Store</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[10px] text-slate-400 font-bold uppercase ml-1">Date Range</label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className={`w-[260px] justify-start text-left font-normal bg-white ${!date && "text-slate-500"}`}>
                <Calendar className="w-4 h-4 mr-2 text-slate-400" />
                {date?.from ? (
                  date.to ? (
                    <>
                      {format(date.from, "LLL dd, y")} - {format(date.to, "LLL dd, y")}
                    </>
                  ) : (
                    format(date.from, "LLL dd, y")
                  )
                ) : (
                  <span className="text-slate-500">Pick a date range</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <CalendarComponent
                initialFocus
                mode="range"
                defaultMonth={date?.from}
                selected={date}
                onSelect={setDate}
                numberOfMonths={2}
              />
            </PopoverContent>
          </Popover>
        </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {stats.map((stat, index) => (
          <div key={index} className={`${stat.color} rounded-2xl p-5 text-white shadow-lg`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-white/70 mb-1">{stat.label}</p>
                <p className="text-2xl font-bold">
                  {stat.value}
                </p>
                <p className="text-xs text-white/60 mt-1">{stat.description}</p>
              </div>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${stat.iconBg}`}>
                <stat.icon className="w-5 h-5 text-white" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Revenue Distribution */}
      <div className="card-elevated p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-6">
          Revenue Distribution
        </h3>

        {/* Visual Bar */}
        <div className="flex items-center h-12 rounded-2xl overflow-hidden shadow-inner bg-slate-100">
          {totalRev > 0 && (
            <>
              <div
                className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 flex items-center justify-center text-xs font-bold text-white relative group transition-all"
                style={{ width: `${netPlatformPct}%`, minWidth: netPlatformPct > 5 ? 'auto' : '0' }}
              >
                {netPlatformPct > 8 && 'Platform'}
              </div>
              <div
                className="h-full bg-gradient-to-r from-amber-500 to-amber-400 flex items-center justify-center text-xs font-bold text-white relative group transition-all"
                style={{ width: `${resellerPct}%`, minWidth: resellerPct > 5 ? 'auto' : '0' }}
              >
                {resellerPct > 8 && 'Resellers'}
              </div>
              <div
                className="h-full bg-gradient-to-r from-blue-500 to-blue-400 flex items-center justify-center text-xs font-bold text-white relative group transition-all"
                style={{ width: `${distributorPct}%`, minWidth: distributorPct > 5 ? 'auto' : '0' }}
              >
                {distributorPct > 8 && 'Distributors'}
              </div>
            </>
          )}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-6 mt-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
            <span className="text-sm text-slate-600">Platform: <strong>${netPlatformShare.toFixed(2)}</strong> ({netPlatformPct.toFixed(1)}%)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-amber-500"></div>
            <span className="text-sm text-slate-600">Resellers: <strong>${(platformStats.reseller_share || 0).toFixed(2)}</strong> ({resellerPct.toFixed(1)}%)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-blue-500"></div>
            <span className="text-sm text-slate-600">Distributors: <strong>${(platformStats.distributor_share || 0).toFixed(2)}</strong> ({distributorPct.toFixed(1)}%)</span>
          </div>
        </div>
      </div>

      {/* Earnings Detailed View */}
      <div className="card-elevated overflow-hidden">
        <Tabs defaultValue="resellers" className="w-full">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-900">
              Detailed Earnings
            </h3>
            <TabsList className="bg-slate-100 p-1">
              <TabsTrigger value="resellers" className="px-4 py-1.5 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
                Resellers ({resellerEarnings.length})
              </TabsTrigger>
              <TabsTrigger value="distributors" className="px-4 py-1.5 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
                Distributors ({distributorEarnings.length})
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Reseller Tab */}
          <TabsContent value="resellers" className="m-0 border-none">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold">Store</TableHead>
                  <TableHead className="font-semibold text-right">Total Sales</TableHead>
                  <TableHead className="font-semibold text-right">Earnings</TableHead>
                  <TableHead className="font-semibold text-right">Pending Payout</TableHead>
                  <TableHead className="font-semibold text-center">Orders</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {resellerEarnings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-12 text-slate-500">
                      <div className="flex flex-col items-center gap-2">
                        <ShoppingCart className="w-8 h-8 text-slate-300" />
                        <p>No reseller earnings found with current filters</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  resellerEarnings.map((earning) => (
                    <TableRow key={earning.store_id || earning.subdomain} className="table-row">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
                            <Building2 className="w-4 h-4 text-amber-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{earning.business_name}</p>
                            <p className="text-[10px] text-slate-500 font-mono uppercase">{earning.subdomain}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        ${(earning.total_sales || 0).toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right text-emerald-600 font-bold">
                        ${(earning.total_earnings || 0).toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                          ${(earning.pending_payout || 0).toFixed(2)}
                        </span>
                      </TableCell>
                      <TableCell className="text-center font-medium text-slate-600">
                        {earning.order_count || 0}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TabsContent>

          {/* Distributor Tab */}
          <TabsContent value="distributors" className="m-0 border-none">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold">Distributor</TableHead>
                  <TableHead className="font-semibold text-right">Total Earnings</TableHead>
                  <TableHead className="font-semibold text-right">Pending Payouts</TableHead>
                  <TableHead className="font-semibold text-center">Orders Involved</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {distributorEarnings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-12 text-slate-500">
                      <div className="flex flex-col items-center gap-2">
                        <Layers className="w-8 h-8 text-slate-300" />
                        <p>No distributor commissions found with current filters</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  distributorEarnings.map((earning) => (
                    <TableRow key={earning.distributor_id} className="table-row">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                            <Users className="w-4 h-4 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{earning.name}</p>
                            <p className="text-xs text-slate-500">{earning.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-bold text-blue-600">
                        ${(earning.total_earnings || 0).toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                          ${(earning.available_balance || 0).toFixed(2)}
                        </span>
                      </TableCell>
                      <TableCell className="text-center font-medium text-slate-600">
                        {earning.order_count || 0}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
